import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { TechWatch, TechWatchSource } from '../services/techWatchService';
import { createWatch, updateWatch, deleteWatch, getWatch } from '../services/techWatchService';

interface TechWatchConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  subProjectId: string;
  onSaved?: (watch: TechWatch) => void;
  onDeleted?: () => void;
}

function blankRss(): TechWatchSource { return { type: 'rss', url: '', label: '' }; }
function blankArxiv(): TechWatchSource { return { type: 'arxiv', url: '', label: '', config: { query: '', categories: [], max_items: 20 } }; }
function blankOpenalex(): TechWatchSource { return { type: 'openalex', url: '', label: '', config: { query: '', issns: [], max_items: 20 } }; }

function isValid(src: TechWatchSource): boolean {
  if (src.type === 'arxiv') return !!src.config?.query?.trim();
  if (src.type === 'openalex') return !!src.config?.query?.trim();
  return !!src.url?.trim();
}

export function TechWatchConfigModal({
  isOpen, onClose, subProjectId, onSaved, onDeleted,
}: TechWatchConfigModalProps) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [existingWatch, setExistingWatch] = useState<TechWatch | null>(null);
  const [sources, setSources] = useState<TechWatchSource[]>([blankRss()]);
  const [isActive, setIsActive] = useState(true);
  const [opmlPodcasts, setOpmlPodcasts] = useState<{ url: string; label: string }[] | null>(null);
  const [selectedOpml, setSelectedOpml] = useState<Set<number>>(new Set());
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    setLoading(true);
    getWatch(subProjectId)
      .then(res => {
        const w = res.data;
        setExistingWatch(w);
        setSources(w.sources.length > 0 ? w.sources : [blankRss()]);
        setIsActive(w.is_active);
      })
      .catch(() => {
        setExistingWatch(null);
        setSources([blankRss()]);
        setIsActive(true);
      })
      .finally(() => setLoading(false));
  }, [isOpen, subProjectId]);

  if (!isOpen) return null;

  const validSources = sources.filter(isValid);

  const handleSave = async () => {
    if (validSources.length === 0) return;
    setSaving(true);
    try {
      let watch: TechWatch;
      if (existingWatch) {
        const res = await updateWatch(existingWatch.id, { sources: validSources, is_active: isActive });
        watch = res.data;
      } else {
        const res = await createWatch(subProjectId, validSources);
        watch = res.data;
      }
      setExistingWatch(watch);
      onSaved?.(watch);
      onClose();
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((e: { loc?: string[]; msg?: string }) => `${e.loc?.slice(1).join('.') ?? ''}: ${e.msg ?? e}`).join('\n')
        : (typeof detail === 'string' ? detail : 'Failed to save');
      alert(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!existingWatch || !confirm(t('techWatch.confirmDelete'))) return;
    try {
      await deleteWatch(existingWatch.id);
      setExistingWatch(null);
      setSources([blankRss()]);
      onDeleted?.();
      onClose();
    } catch {
      alert('Failed to delete');
    }
  };

  const addSource = (type: 'rss' | 'arxiv' | 'openalex') =>
    setSources([...sources, type === 'arxiv' ? blankArxiv() : type === 'openalex' ? blankOpenalex() : blankRss()]);

  const updateSource = (i: number, patch: Partial<TechWatchSource>) => {
    const next = [...sources];
    next[i] = { ...next[i], ...patch };
    setSources(next);
  };

  const updateSourceConfig = (i: number, key: string, val: unknown) => {
    const next = [...sources];
    next[i] = { ...next[i], config: { ...(next[i].config ?? {}), [key]: val } };
    setSources(next);
  };

  const changeSourceType = (i: number, type: 'rss' | 'arxiv' | 'openalex') => {
    setSources(prev => {
      const next = [...prev];
      next[i] = type === 'arxiv' ? { ...blankArxiv(), label: next[i].label }
        : type === 'openalex' ? { ...blankOpenalex(), label: next[i].label }
        : { ...blankRss(), label: next[i].label };
      return next;
    });
  };

  const removeSource = (i: number) => {
    const next = sources.filter((_, idx) => idx !== i);
    setSources(next.length > 0 ? next : [blankRss()]);
  };

  const handleOpmlFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target?.result as string;
      const parser = new DOMParser();
      const doc = parser.parseFromString(text, 'application/xml');
      const outlines = Array.from(doc.querySelectorAll('outline[type="rss"]'));
      const podcasts = outlines
        .map(el => ({ url: el.getAttribute('xmlUrl') || '', label: el.getAttribute('text') || el.getAttribute('title') || '' }))
        .filter(p => p.url);
      setOpmlPodcasts(podcasts);
      setSelectedOpml(new Set(podcasts.map((_, i) => i)));
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const toggleOpml = (i: number) => {
    setSelectedOpml(prev => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i); else next.add(i);
      return next;
    });
  };

  const addOpmlSelected = () => {
    if (!opmlPodcasts) return;
    const existingUrls = new Set(sources.map(s => s.url));
    const toAdd = opmlPodcasts
      .filter((_, i) => selectedOpml.has(i))
      .map(p => ({ type: 'rss' as const, url: p.url, label: p.label }))
      .filter(s => !existingUrls.has(s.url));
    const currentSources = sources.filter(s => isValid(s));
    setSources([...currentSources, ...toAdd]);
    setOpmlPodcasts(null);
    setSelectedOpml(new Set());
  };

  return (
    <div className="create-kb-modal-overlay" onClick={onClose}>
      <div className="create-kb-modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 580 }}>
        <div className="create-kb-modal-header">
          <h2>{existingWatch ? t('techWatch.editWatch') : t('techWatch.defineWatch')}</h2>
          <button className="create-kb-close-btn" onClick={onClose}>&times;</button>
        </div>

        {loading ? (
          <div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>
        ) : (
          <>
            <div className="create-kb-modal-body" style={{ padding: '1rem 1.5rem', overflowY: 'auto', maxHeight: '60vh' }}>
              {/* Top controls */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
                <input type="file" ref={fileInputRef} accept=".opml,.xml" onChange={handleOpmlFile} style={{ display: 'none' }} />
                <button type="button" onClick={() => fileInputRef.current?.click()}
                  style={{ background: 'none', border: '1px dashed #9ca3af', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', color: '#6b7280', fontSize: 13 }}>
                  {t('techWatch.importOpml')}
                </button>
                <button type="button" onClick={() => addSource('rss')}
                  style={{ background: 'none', border: '1px dashed #9ca3af', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', color: '#6b7280', fontSize: 13 }}>
                  + RSS
                </button>
                <button type="button" onClick={() => addSource('arxiv')}
                  style={{ background: 'none', border: '1px dashed #7c3aed', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', color: '#7c3aed', fontSize: 13 }}>
                  + arXiv
                </button>
                <button type="button" onClick={() => addSource('openalex')}
                  style={{ background: 'none', border: '1px dashed #0369a1', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', color: '#0369a1', fontSize: 13 }}>
                  + OpenAlex
                </button>
                {existingWatch && (
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13, marginLeft: 'auto' }}>
                    <input type="checkbox" checked={isActive} onChange={e => setIsActive(e.target.checked)} />
                    {t('techWatch.sourceActive')}
                  </label>
                )}
              </div>

              {/* OPML checklist */}
              {opmlPodcasts !== null && (
                <div style={{ border: '1px solid #e5e7eb', borderRadius: 6, marginBottom: 12, overflow: 'hidden' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 10px', background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                    <span style={{ fontSize: 13, fontWeight: 500 }}>
                      {opmlPodcasts.length === 0 ? t('techWatch.opmlNone') : t('techWatch.opmlFound', { count: opmlPodcasts.length })}
                    </span>
                    <div style={{ display: 'flex', gap: 10 }}>
                      <button type="button" onClick={() => setSelectedOpml(new Set(opmlPodcasts.map((_, i) => i)))}
                        style={{ fontSize: 12, background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer' }}>{t('techWatch.opmlSelectAll')}</button>
                      <button type="button" onClick={() => setSelectedOpml(new Set())}
                        style={{ fontSize: 12, background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer' }}>{t('techWatch.opmlDeselectAll')}</button>
                      <button type="button" onClick={() => { setOpmlPodcasts(null); setSelectedOpml(new Set()); }}
                        style={{ fontSize: 12, background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer' }}>{t('techWatch.cancel')}</button>
                    </div>
                  </div>
                  <div style={{ maxHeight: 200, overflowY: 'auto', padding: '4px 0' }}>
                    {opmlPodcasts.map((p, i) => (
                      <label key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 10px', cursor: 'pointer', fontSize: 13 }}>
                        <input type="checkbox" checked={selectedOpml.has(i)} onChange={() => toggleOpml(i)} />
                        {p.label || p.url}
                      </label>
                    ))}
                  </div>
                  {opmlPodcasts.length > 0 && (
                    <div style={{ padding: '6px 10px', borderTop: '1px solid #e5e7eb', textAlign: 'right' }}>
                      <button type="button" onClick={addOpmlSelected} disabled={selectedOpml.size === 0}
                        className="create-kb-btn create-kb-btn-primary" style={{ fontSize: 13 }}>
                        {t('techWatch.opmlAddSelected', { count: selectedOpml.size })}
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Source rows */}
              {sources.map((src, i) => (
                <div key={i} style={{ border: '1px solid #e5e7eb', borderRadius: 6, padding: '8px 10px', marginBottom: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: src.type === 'arxiv' ? 6 : 0 }}>
                    <select
                      value={src.type}
                      onChange={e => changeSourceType(i, e.target.value as 'rss' | 'arxiv' | 'openalex')}
                      style={{ fontSize: 13, padding: '2px 6px', borderRadius: 4, border: '1px solid #d1d5db', background: src.type === 'arxiv' ? '#ede9fe' : src.type === 'openalex' ? '#e0f2fe' : '#fff', color: src.type === 'arxiv' ? '#7c3aed' : src.type === 'openalex' ? '#0369a1' : '#111' }}
                    >
                      <option value="rss">RSS</option>
                      <option value="arxiv">arXiv</option>
                      <option value="openalex">OpenAlex</option>
                    </select>

                    {src.type === 'rss' && (
                      <input type="url" placeholder={t('techWatch.rssUrlPlaceholder')}
                        value={src.url || ''} onChange={e => updateSource(i, { url: e.target.value })}
                        style={{ flex: 2, fontSize: 13 }} />
                    )}

                    <input type="text" placeholder={t('techWatch.sourceLabel')}
                      value={src.label || ''} onChange={e => updateSource(i, { label: e.target.value })}
                      style={{ flex: 1, fontSize: 13 }} />

                    {sources.length > 1 && (
                      <button type="button" onClick={() => removeSource(i)}
                        style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: 18, lineHeight: 1 }}>
                        &times;
                      </button>
                    )}
                  </div>

                  {src.type === 'arxiv' && (
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <input type="text" placeholder={t('techWatch.arxivQuery')}
                        value={src.config?.query || ''}
                        onChange={e => updateSourceConfig(i, 'query', e.target.value)}
                        style={{ flex: 2, fontSize: 13, minWidth: 160 }} />
                      <input type="text" placeholder={t('techWatch.arxivCategories')}
                        value={(src.config?.categories ?? []).join(', ')}
                        onChange={e => updateSourceConfig(i, 'categories', e.target.value.split(',').map(c => c.trim()).filter(Boolean))}
                        style={{ flex: 1, fontSize: 13, minWidth: 100 }} />
                      <input type="number" placeholder={t('techWatch.arxivMaxItems')}
                        value={src.config?.max_items ?? 20} min={1} max={50}
                        onChange={e => updateSourceConfig(i, 'max_items', parseInt(e.target.value) || 20)}
                        style={{ width: 70, fontSize: 13 }} />
                    </div>
                  )}

                  {src.type === 'openalex' && (
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <input type="text" placeholder={t('techWatch.openalexQuery')}
                        value={src.config?.query || ''}
                        onChange={e => updateSourceConfig(i, 'query', e.target.value)}
                        style={{ flex: 2, fontSize: 13, minWidth: 160 }} />
                      <input type="text" placeholder={t('techWatch.openalexIssns')}
                        value={(src.config?.issns ?? []).join(', ')}
                        onChange={e => updateSourceConfig(i, 'issns', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
                        style={{ flex: 1, fontSize: 13, minWidth: 120 }} />
                      <input type="number" placeholder={t('techWatch.arxivMaxItems')}
                        value={src.config?.max_items ?? 20} min={1} max={100}
                        onChange={e => updateSourceConfig(i, 'max_items', parseInt(e.target.value) || 20)}
                        style={{ width: 70, fontSize: 13 }} />
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="create-kb-modal-footer" style={{ display: 'flex', justifyContent: 'space-between', padding: '1rem 1.5rem', borderTop: '1px solid #e5e7eb' }}>
              <div>
                {existingWatch && (
                  <button type="button" onClick={handleDelete}
                    className="create-kb-btn create-kb-btn-secondary"
                    style={{ color: '#dc2626', borderColor: '#dc2626' }}>
                    {t('techWatch.deleteWatch')}
                  </button>
                )}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="button" onClick={onClose} className="create-kb-btn create-kb-btn-secondary" disabled={saving}>
                  {t('techWatch.cancel')}
                </button>
                <button type="button" onClick={handleSave}
                  className="create-kb-btn create-kb-btn-primary"
                  disabled={saving || validSources.length === 0}>
                  {saving ? '...' : t('techWatch.save')}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
